import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Drawer, Table, Input, InputNumber, Button, Space, message, Alert, Tag, Upload } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import ProjectQuotaSettings from './ProjectQuotaSettings';
import { DoctorQuotaItem, getProjectDoctorQuotas, updateProjectDoctorQuotas, downloadDoctorQuotaTemplate, importDoctorQuotas } from '../services/project';

interface DoctorQuotaDrawerProps {
  open: boolean;
  projectId: number | null;
  projectName?: string;
  onClose: () => void;
}
type QuotaValues = Pick<DoctorQuotaItem, 'max_submissions' | 'monthly_max_submissions'>;
type QuotaField = keyof QuotaValues;
const PAGE_SIZE = 20;
const emptyQuota: QuotaValues = { max_submissions: null, monthly_max_submissions: null };
const normalize = (value: number | null): number | null => value && value > 0 ? value : null;

const DoctorQuotaDrawer: React.FC<DoctorQuotaDrawerProps> = ({ open, projectId, projectName, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [list, setList] = useState<DoctorQuotaItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [keyword, setKeyword] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [selectedKeys, setSelectedKeys] = useState<React.Key[]>([]);
  const [batch, setBatch] = useState<QuotaValues>(emptyQuota);
  const [edits, setEdits] = useState<Record<number, QuotaValues>>({});
  const [importErrors, setImportErrors] = useState<string[]>([]);
  const requestId = useRef(0);
  const pendingCount = Object.keys(edits).length;
  const busy = loading || saving;

  const fetchList = useCallback(async (targetPage: number, targetKeyword: string) => {
    if (!projectId) return;
    const id = ++requestId.current;
    setLoading(true);
    setList([]);
    setSelectedKeys([]);
    try {
      const res = await getProjectDoctorQuotas(projectId, { page: targetPage, pageSize: PAGE_SIZE, keyword: targetKeyword });
      if (id !== requestId.current) return;
      if (!res.success) throw new Error(res.message || '获取医生配额失败');
      setList(res.data.list || []);
      setTotal(res.data.total || 0);
    } catch (error: any) {
      if (id === requestId.current) message.error(error?.response?.data?.message || error.message || '获取医生配额失败');
    } finally {
      if (id === requestId.current) setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    if (open && projectId) {
      setPage(1); setKeyword(''); setSearchInput(''); setBatch(emptyQuota);
      setEdits({}); setImportErrors([]);
      fetchList(1, '');
    }
    return () => { requestId.current += 1; };
  }, [open, projectId, fetchList]);

  const currentValues = (record: DoctorQuotaItem): QuotaValues => edits[record.doctor_id] || {
    max_submissions: normalize(record.max_submissions), monthly_max_submissions: normalize(record.monthly_max_submissions)
  };
  const changeValue = (record: DoctorQuotaItem, field: QuotaField, value: number | null) => {
    setEdits(previous => ({ ...previous, [record.doctor_id]: { ...currentValues(record), [field]: normalize(value) } }));
  };
  const handleSave = async () => {
    if (!projectId || !pendingCount || saving) return;
    setSaving(true);
    try {
      const quotas = Object.entries(edits).map(([doctorId, values]) => ({ doctor_id: Number(doctorId), ...values }));
      const res = await updateProjectDoctorQuotas(projectId, quotas);
      if (!res.success) throw new Error(res.message);
      message.success(res.message || '保存成功');
      setEdits({});
      await fetchList(page, keyword);
    } catch (error: any) { message.error(error?.response?.data?.message || error.message || '保存失败'); }
    finally { setSaving(false); }
  };
  const handleDownload = async () => {
    if (!projectId) return;
    setDownloading(true);
    try {
      const blob = await downloadDoctorQuotaTemplate(projectId);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url; link.download = '医生配额导入模板.xlsx';
      document.body.appendChild(link); link.click(); link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (_) { message.error('模板下载失败'); }
    finally { setDownloading(false); }
  };
  const handleImport = async (file: File) => {
    if (!projectId || pendingCount || saving) return false;
    if (!/\.xlsx?$/i.test(file.name) || file.size > 5 * 1024 * 1024) {
      message.error('请选择不超过5MB的xls或xlsx文件'); return false;
    }
    setSaving(true); setImportErrors([]);
    try {
      const res = await importDoctorQuotas(projectId, file);
      if (!res.success) throw new Error(res.message);
      message.success(res.message || '导入成功');
      await fetchList(page, keyword);
    } catch (error: any) {
      const data = error?.response?.data;
      setImportErrors(data?.errors?.length ? data.errors : [data?.message || error.message || '导入失败']);
      message.error(data?.message || error.message || '导入失败');
    } finally { setSaving(false); }
    return false;
  };
  const limitColumn = (field: QuotaField, title: string, countField: 'submitted_count' | 'monthly_submitted_count'): ColumnsType<DoctorQuotaItem>[number] => ({
    title, key: field, width: 185,
    render: (_, record) => {
      const value = currentValues(record)[field];
      const reached = value !== null && record[countField] >= value;
      return <Space direction="vertical" size={2}>
        <InputNumber min={0} max={2147483647} precision={0} value={value} disabled={busy}
          placeholder={field === "monthly_max_submissions" ? "留空或0使用项目默认" : "留空或0不限制"} onChange={v => changeValue(record, field, v === null ? null : Number(v))} />
        {edits[record.doctor_id] && <span style={{ color: '#faad14', fontSize: 12 }}>待保存</span>}
        {field === 'monthly_max_submissions' && value === null && <span style={{ fontSize: 12 }}>当前生效：{record.effective_monthly_limit ?? '未配置项目默认'}</span>}
        {reached && <span style={{ color: '#ff4d4f', fontSize: 12 }}>已达上限，无法继续提交</span>}
      </Space>;
    }
  });
  const columns: ColumnsType<DoctorQuotaItem> = [
    { title: '医生', key: 'doctor', width: 170, render: (_, r) => <div>{r.doctor_name}<div style={{ color: '#999', fontSize: 12 }}>{r.doctor_phone} / ID: {r.doctor_id}</div></div> },
    { title: '医院 / 科室', key: 'hospital', width: 190, render: (_, r) => <div>{r.hospital_name || '-'}<div style={{ color: '#999', fontSize: 12 }}>{r.department_name || '-'}</div></div> },
    { title: '累计已提交', dataIndex: 'submitted_count', width: 100, render: count => <Tag>{count} 次</Tag> },
    { title: '本月已提交', dataIndex: 'monthly_submitted_count', width: 100, render: count => <Tag color="blue">{count} 次</Tag> },
    limitColumn('max_submissions', '项目累计上限', 'submitted_count'),
    limitColumn('monthly_max_submissions', '每月上限', 'monthly_submitted_count')
  ];

  return <Drawer title={'医生提交次数配额' + (projectName ? ' - ' + projectName : '')} open={open}
    onClose={() => { if (!saving) onClose(); }} width={1100} destroyOnClose closable={!saving} maskClosable={!saving}
    extra={<Space>
      <Button disabled={saving || !pendingCount} onClick={() => setEdits({})}>放弃修改</Button>
      <Button type="primary" loading={saving} disabled={!pendingCount || loading} onClick={handleSave}>保存修改{pendingCount ? '（' + pendingCount + '）' : ''}</Button>
    </Space>}>
    {projectId && <ProjectQuotaSettings projectId={projectId} onSaved={() => fetchList(page, keyword)} />}
    <Alert type="info" showIcon style={{ marginBottom: 16 }} message="配额说明"
      description="每月上限适用于每个北京时间自然月，无需选择月份。累计上限留空或0表示不限；每月上限留空或0使用项目默认值。项目累计上限、每月上限与项目及问卷其他限制同时生效，任一达到上限即无法提交。" />
    <Space wrap style={{ marginBottom: 8 }}>
      <Button onClick={handleDownload} loading={downloading} disabled={busy || pendingCount > 0}>下载Excel模板（含当前配额）</Button>
      <Upload accept=".xlsx,.xls" showUploadList={false} beforeUpload={handleImport} disabled={busy || pendingCount > 0}>
        <Button loading={saving} disabled={busy || pendingCount > 0}>导入Excel</Button>
      </Upload>
      {pendingCount > 0 && <span>请先保存或放弃页面修改，再导入或下载模板</span>}
    </Space>
    <div style={{ color: '#666', marginBottom: 16 }}>导入会覆盖表内医生的两项上限；累计上限空白或0表示不限，每月上限空白或0使用项目默认值，未列入的医生保持原配额。</div>
    {importErrors.length > 0 && <Alert type="error" showIcon message="导入失败，未修改配额" style={{ marginBottom: 16 }}
      description={<div style={{ maxHeight: 200, overflowY: 'auto' }}>{importErrors.map((error, index) => <div key={index}>{error}</div>)}</div>} />}
    <Space wrap style={{ marginBottom: 16 }}>
      <Input placeholder="按姓名 / 手机号 / 医院搜索" value={searchInput} onChange={e => setSearchInput(e.target.value)} disabled={saving}
        onPressEnter={() => { setPage(1); setKeyword(searchInput.trim()); fetchList(1, searchInput.trim()); }} style={{ width: 240 }} allowClear />
      <Button disabled={busy} onClick={() => { setPage(1); setKeyword(searchInput.trim()); fetchList(1, searchInput.trim()); }}>搜索</Button>
    </Space>
    <Space wrap style={{ marginBottom: 16 }}>
      <span>批量设置：</span>
      <InputNumber min={0} max={2147483647} precision={0} placeholder="项目累计上限" value={batch.max_submissions} disabled={busy}
        onChange={v => setBatch({ ...batch, max_submissions: normalize(v) })} />
      <InputNumber min={0} max={2147483647} precision={0} placeholder="每月上限" value={batch.monthly_max_submissions} disabled={busy}
        onChange={v => setBatch({ ...batch, monthly_max_submissions: normalize(v) })} />
      <Button disabled={busy || !selectedKeys.length} onClick={() => {
        const next = { ...edits }; selectedKeys.forEach(key => { next[Number(key)] = { ...batch }; }); setEdits(next);
        message.info('已应用两项上限，保存后生效；月额度留空使用项目默认值，累计额度留空不限');
      }}>应用到选中的 {selectedKeys.length} 位</Button>
    </Space>
    <Table<DoctorQuotaItem> rowKey="doctor_id" columns={columns} dataSource={list} loading={loading} size="small" scroll={{ x: 1000 }}
      rowSelection={{ selectedRowKeys: selectedKeys, onChange: keys => setSelectedKeys(keys), getCheckboxProps: () => ({ disabled: busy }) }}
      pagination={{ current: page, pageSize: PAGE_SIZE, total, showSizeChanger: false, disabled: busy, showTotal: t => '共 ' + t + ' 位白名单医生',
        onChange: next => { setPage(next); fetchList(next, keyword); } }} />
  </Drawer>;
};
export default DoctorQuotaDrawer;

